// src/storage/MemoryAdapter.js

import StorageAdapter from './StorageAdapter.js';

/**
 * In-memory storage adapter for EventIndexer.
 * Used as fallback when IndexedDB unavailable (SSR, private browsing).
 * No persistence across sessions.
 */
class MemoryAdapter extends StorageAdapter {
  constructor() {
    super();
    this.stores = new Map(); // eventType -> Map<id, event>
    this.syncState = null;
    this.version = 0;
    this.config = null;
  }

  async initialize(config) {
    this.config = config;

    // Create store for each event type
    for (const eventType of config.eventTypes) {
      this.stores.set(eventType.name, new Map());
    }

    // Check version for migration
    if (config.version > this.version) {
      await this.clear();
      this.version = config.version;
    }
  }

  async putEvents(events) {
    for (const event of events) {
      const store = this.stores.get(event.type);
      if (!store) {
        // Lazily create store for unknown event types
        this.stores.set(event.type, new Map());
      }
      this.stores.get(event.type).set(event.id, event);
    }
  }

  async getEvent(type, id) {
    const store = this.stores.get(type);
    if (!store) return null;
    return store.get(id) || null;
  }

  async queryEvents(type, options = {}) {
    const store = this.stores.get(type);
    if (!store) {
      return { events: [], total: 0, hasMore: false };
    }

    let events = Array.from(store.values());

    // Apply where filters
    if (options.where) {
      events = events.filter(event => this._matchesWhere(event, options.where));
    }

    const total = events.length;

    // Sort
    const orderBy = options.orderBy || 'blockNumber';
    const order = options.order || 'desc';
    events.sort((a, b) => {
      const aVal = this._getNestedValue(a, orderBy);
      const bVal = this._getNestedValue(b, orderBy);
      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });

    // Pagination
    const offset = options.offset || 0;
    const limit = options.limit || 100;
    const paged = events.slice(offset, offset + limit);

    return {
      events: paged,
      total,
      hasMore: offset + limit < total
    };
  }

  async countEvents(type, where) {
    const store = this.stores.get(type);
    if (!store) return 0;

    if (!where) return store.size;

    let count = 0;
    for (const event of store.values()) {
      if (this._matchesWhere(event, where)) {
        count++;
      }
    }
    return count;
  }

  async deleteEvents(type, ids) {
    const store = this.stores.get(type);
    if (!store) return;

    for (const id of ids) {
      store.delete(id);
    }
  }

  async deleteEventsAfterBlock(blockNumber) {
    for (const store of this.stores.values()) {
      for (const [id, event] of store.entries()) {
        if (event.blockNumber > blockNumber) {
          store.delete(id);
        }
      }
    }
  }

  async getSyncState() {
    return this.syncState;
  }

  async setSyncState(state) {
    this.syncState = { ...state };
  }

  async getVersion() {
    return this.version;
  }

  async setVersion(version) {
    this.version = version;
  }

  async clear() {
    for (const store of this.stores.values()) {
      store.clear();
    }
    this.syncState = null;
  }

  async close() {
    // No-op for memory adapter
  }

  // Helper: check if event matches where clause
  _matchesWhere(event, where) {
    for (const [key, value] of Object.entries(where)) {
      const eventValue = this._getNestedValue(event, key);

      // Handle array values (OR condition)
      if (Array.isArray(value)) {
        if (!value.includes(eventValue)) return false;
      }
      // Handle comparison operators
      else if (typeof value === 'object' && value !== null) {
        if (value.$gt !== undefined && !(eventValue > value.$gt)) return false;
        if (value.$gte !== undefined && !(eventValue >= value.$gte)) return false;
        if (value.$lt !== undefined && !(eventValue < value.$lt)) return false;
        if (value.$lte !== undefined && !(eventValue <= value.$lte)) return false;
        if (value.$ne !== undefined && eventValue === value.$ne) return false;
      }
      // Direct equality (case-insensitive for addresses)
      else {
        const normalizedEvent = typeof eventValue === 'string' ? eventValue.toLowerCase() : eventValue;
        const normalizedValue = typeof value === 'string' ? value.toLowerCase() : value;
        if (normalizedEvent !== normalizedValue) return false;
      }
    }
    return true;
  }

  // Helper: get nested value from object using dot notation
  _getNestedValue(obj, path) {
    // Check indexed params first, then data, then direct
    if (path.startsWith('indexed.')) {
      return obj.indexed?.[path.slice(8)];
    }
    if (path.startsWith('data.')) {
      return obj.data?.[path.slice(5)];
    }

    // Try indexed, then data, then direct property
    if (obj.indexed?.[path] !== undefined) return obj.indexed[path];
    if (obj.data?.[path] !== undefined) return obj.data[path];
    return obj[path];
  }
}

export default MemoryAdapter;
